import { Drawer, List, Menu, PopoverPosition } from '@mui/material';

import { LatLng } from 'shared/types';
import { useMemo } from 'react';
import { CopyPositionMenuItem } from './CopyPositionMenuItem';
import { DropPinMenuItem } from './DropPinMenuItem';
import { MeasureDistanceMenuItem } from './MeasureDistanceMenuItem';
import { LightningToolsMenuItem } from './LightningToolsMenuItem';
import { MapToolsMenuItemProps } from './types';

export type ContextMenuItemKey =
  | 'copyPosition'
  | 'dropPin'
  | 'lightningTools'
  | 'measureDistance';

type MapToolsContextMenuProps =
  | {
      variant: 'menu';
      onClose: () => void;
      pointCoordinates: LatLng | null;
      exclude?: ContextMenuItemKey[];
      anchorPosition: PopoverPosition;
    }
  | {
      variant: 'drawer';
      onClose: () => void;
      pointCoordinates: LatLng | null;
      exclude?: ContextMenuItemKey[];
    };

type ContextMenuItemType = {
  key: ContextMenuItemKey;
  Component: (props: MapToolsMenuItemProps) => JSX.Element | null;
};

const MENU_ITEMS: ContextMenuItemType[] = [
  { key: 'copyPosition', Component: CopyPositionMenuItem },
  { key: 'dropPin', Component: DropPinMenuItem },
  { key: 'lightningTools', Component: LightningToolsMenuItem },
  { key: 'measureDistance', Component: MeasureDistanceMenuItem },
];

export const MapToolsContextMenu = (
  props: MapToolsContextMenuProps,
): JSX.Element | null => {
  const { pointCoordinates, onClose, variant, exclude = [] } = props;

  const filteredItems = useMemo(() => {
    return MENU_ITEMS.filter((item) => !exclude.includes(item.key));
  }, [exclude]);

  if (!pointCoordinates) {
    return null;
  }

  if (variant === 'drawer') {
    return (
      <Drawer
        anchor="bottom"
        open={pointCoordinates !== null}
        onClose={onClose}
        // Render above LayersNavControl's AppBar, which also uses theme.zIndex.modal.
        sx={(theme) => ({ zIndex: theme.zIndex.modal + 1 })}
      >
        <List sx={{ marginBottom: 'env(safe-area-inset-bottom)' }}>
          {filteredItems.map((item) => (
            <item.Component
              key={item.key}
              position={pointCoordinates}
              onClick={onClose}
              padding={2}
              variant={variant}
            />
          ))}
        </List>
      </Drawer>
    );
  }
  const { anchorPosition } = props;
  return (
    <Menu
      open={pointCoordinates !== null}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
    >
      {filteredItems.map((item) => (
        <item.Component
          key={item.key}
          position={pointCoordinates}
          onClick={onClose}
          padding={1}
          variant={variant}
        />
      ))}
    </Menu>
  );
};
